import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { RotateCcw, Loader2, Mail, ImagePlus } from "lucide-react";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const valid = (c, fb) => (HEX_RE.test(c || "") ? c : fb);

export const isLight = (hex) => {
  const m = (hex || "").replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(m)) return false;
  const r = parseInt(m.slice(0, 2), 16), g = parseInt(m.slice(2, 4), 16), b = parseInt(m.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62;
};

const shade = (hex, amt) => {
  const m = hex.replace("#", "");
  const f = (i) => Math.max(0, Math.min(255, Math.round(parseInt(m.slice(i, i + 2), 16) * (1 + amt))));
  return `#${[f(0), f(2), f(4)].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};

const hexToHsl = (hex) => {
  const m = (hex || "").replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(m)) return null;
  const r = parseInt(m.slice(0, 2), 16) / 255, g = parseInt(m.slice(2, 4), 16) / 255, b = parseInt(m.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0); else if (max === g) h = (b - r) / d + 2; else h = (r - g) / d + 4;
    h *= 60;
  }
  return { h, s, l };
};

const hslToHex = ({ h, s, l }) => {
  h = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s, x = c * (1 - Math.abs(((h / 60) % 2) - 1)), m = l - c / 2;
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  const to = (v) => Math.round((v + m) * 255).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`.toUpperCase();
};

export const suggestPalette = (base) => {
  const hsl = hexToHsl(base) || hexToHsl("#0037ff");
  return {
    color_acento: hslToHex({ ...hsl, l: Math.max(0.18, hsl.l - 0.16), s: Math.min(1, hsl.s + 0.05) }),
    color_header: hslToHex({ h: hsl.h - 6, s: hsl.s, l: Math.min(0.6, hsl.l) }),
    color_suave: hslToHex({ h: hsl.h, s: Math.min(0.45, hsl.s), l: 0.97 }),
  };
};

const LogoUpload = ({ label, hint, value, onChange, testid }) => {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Solo imágenes (PNG/JPG/SVG)");
    if (file.size > 5 * 1024 * 1024) return toast.error("Máximo 5 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/viajeros-ext/upload-logo`, fd, { withCredentials: true });
      onChange(r.data.logo_id);
      toast.success("Logo subido");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir el logo");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="space-y-1.5 flex-1 min-w-0">
      <Label className="text-xs font-bold">{label}</Label>
      {value ? (
        <div className="flex items-center gap-3 border border-slate-200 rounded-xl p-2.5 bg-[repeating-conic-gradient(#f1f5f9_0%_25%,#fff_0%_50%)] bg-[length:14px_14px]">
          <img src={`${API}/viajeros-ext/logos/${value}`} alt="" className="h-10 max-w-[130px] object-contain" />
          <button type="button" className="text-xs text-red-500 font-bold ml-auto flex-shrink-0" onClick={() => onChange(null)} data-testid={`${testid}-remove`}>Quitar</button>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-xl py-4 px-3 text-xs text-slate-500 cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/40 transition-colors text-center"
          onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); }} data-testid={`${testid}-drop`}>
          {uploading ? <Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> : (<>
            <ImagePlus className="w-5 h-5 mb-1 text-slate-400" />
            <span className="font-semibold">Subir logo</span>
            <span className="text-[10px] text-slate-400 mt-0.5">{hint}</span>
          </>)}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={testid} />
        </label>
      )}
    </div>
  );
};

const ColorRow = ({ title, desc, value, fallback, onChange, onReset, highlight, children, testid }) => (
  <div className={`rounded-xl border bg-white p-4 space-y-2.5 ${highlight ? "border-indigo-400 ring-1 ring-indigo-200" : "border-slate-200"}`}>
    <div className="flex items-start justify-between gap-2">
      <div>
        <p className="text-sm font-bold text-slate-800">{title}</p>
        <p className="text-xs text-slate-500">{desc}</p>
      </div>
      {onReset && (
        <button type="button" onClick={onReset} className="text-slate-400 hover:text-slate-600 transition-colors" title="Restablecer sugerido" data-testid={`${testid}-reset`}>
          <RotateCcw className="w-4 h-4" />
        </button>
      )}
    </div>
    <div className="flex items-center gap-2 flex-wrap">
      <input type="color" value={valid(value, fallback)} onChange={(e) => onChange(e.target.value.toUpperCase())}
        className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5 bg-white" data-testid={`${testid}-picker`} />
      <Input className="w-28 font-mono uppercase" value={value || ""} placeholder={fallback}
        onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      {children}
    </div>
  </div>
);

const FakeInput = ({ label }) => (
  <div>
    <p className="text-[10px] font-bold text-slate-500 mb-1">{label} <span className="text-red-400">*</span></p>
    <div className="h-8 rounded-lg border border-slate-200 bg-slate-50/70" />
  </div>
);

const FormPreview = ({ nombre, cliente, logoEmpresa, logoEvento, base, acento, header, suave }) => {
  const light = isLight(header);
  const badge = [cliente, nombre].filter(Boolean).join(" | ") || "Nombre del proyecto";
  return (
    <div className="rounded-2xl overflow-hidden shadow-xl border border-slate-200" data-testid="design-preview">
      <div className="relative overflow-hidden px-5 pt-4 pb-12" style={{ background: `linear-gradient(130deg, ${shade(header, 0.10)}, ${header} 55%, ${shade(header, -0.18)})` }}>
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white opacity-[0.12] blur-2xl pointer-events-none" />
        <div className="flex items-center justify-between gap-3 relative">
          {logoEmpresa
            ? <img src={logoEmpresa} alt="" className="h-8 max-w-[110px] object-contain" />
            : <img src={light ? "/pd-logo-blue.png" : "/pd-logo-white.png"} alt="" className="h-5 object-contain" />}
          <div className="flex gap-1 flex-shrink-0">
            <span className="px-2.5 py-1 rounded-full text-[9px] font-bold bg-white shadow" style={{ color: base }}>ES</span>
            <span className={`px-2.5 py-1 rounded-full text-[9px] font-bold border ${light ? "text-slate-600 border-slate-400/40" : "text-white/70 border-white/30"}`}>EN</span>
          </div>
        </div>
      </div>
      <div style={{ background: suave }} className="px-4 pb-4 space-y-3">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 px-4 py-5 text-center -mt-8 relative z-10">
          {logoEvento && <img src={logoEvento} alt="" className="h-12 mx-auto mb-2.5 object-contain" />}
          <span className="inline-block px-2.5 py-1 rounded-full text-[9px] font-extrabold tracking-[0.12em] uppercase" style={{ background: `${base}14`, color: base }}>{badge}</span>
          <p className="font-extrabold text-slate-900 text-base mt-2 leading-tight">Completa tu perfil de viajero</p>
          <p className="text-[10px] text-slate-500 mt-1">Tus datos nos permiten preparar cada detalle de tu viaje.</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-3.5 py-2.5 border-b border-slate-50 flex items-center gap-2.5" style={{ background: `linear-gradient(90deg, ${base}08, transparent 60%)` }}>
            <span className="w-6 h-6 rounded-lg text-white text-[10px] font-extrabold flex items-center justify-center" style={{ background: base }}>1</span>
            <p className="font-extrabold text-slate-800 text-xs">Información personal</p>
          </div>
          <div className="p-3.5 grid grid-cols-2 gap-2.5">
            <FakeInput label="Nombre(s)" />
            <FakeInput label="Apellido paterno" />
          </div>
          <div className="px-3.5 pb-3.5">
            <label className="flex items-start gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-2.5 py-2">
              <span className="w-3 h-3 rounded-sm mt-0.5 flex-shrink-0" style={{ background: acento }} />
              <span className="text-[10px] text-slate-600">Acepto el <span className="underline font-bold" style={{ color: base }}>aviso de privacidad</span></span>
            </label>
          </div>
        </div>
        <div className="w-full py-2.5 rounded-xl text-white font-extrabold text-xs text-center" style={{ background: `linear-gradient(135deg, ${shade(acento, 0.12)}, ${acento})`, boxShadow: `0 8px 18px -6px ${acento}99` }}>
          Enviar / Submit →
        </div>
      </div>
      <div style={{ background: header }} className="py-3.5 text-center">
        <p className={`text-[8px] tracking-[0.3em] uppercase font-semibold ${light ? "text-slate-500" : "text-white/50"}`}>Powered by</p>
        <img src={light ? "/pd-logo-blue.png" : "/pd-logo-white.png"} alt="PD Travel" className="h-5 mx-auto mt-1.5 object-contain" />
      </div>
    </div>
  );
};

export const DesignEditor = ({ projDialog, setProjDialog }) => {
  const b = projDialog.branding || {};
  const setB = (patch) => setProjDialog({ ...projDialog, branding: { ...b, ...patch } });
  const base = valid(b.color_base, "#0037FF");
  const acento = valid(b.color_acento, base);
  const header = valid(b.color_header, valid(b.color_dark, "#0A1230"));
  const suave = valid(b.color_suave, "#F4F6FB");
  const sug = suggestPalette(base);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          Define un <b>color base</b> y el sistema sugiere los otros 3. Puedes sobrescribir cualquiera. La vista previa de la derecha muestra cómo se aplica.
        </p>
        <div className="flex gap-3 flex-col sm:flex-row">
          <LogoUpload label="Logo de la empresa" hint="Aparece en el header · PNG/JPG/SVG ≤5 MB"
            value={b.logo_empresa_id} onChange={(v) => setB({ logo_empresa_id: v })} testid="logo-empresa" />
          <LogoUpload label="Logo del evento" hint="Aparece arriba del título · PNG/JPG/SVG ≤5 MB"
            value={b.logo_evento_id} onChange={(v) => setB({ logo_evento_id: v })} testid="logo-evento" />
        </div>
        <ColorRow highlight title="🎨 Color base" desc="Números de sección, enlaces y acentos de marca"
          value={b.color_base} fallback="#0037FF" testid="design-color-base"
          onChange={(v) => setB({ color_base: v })}>
          <span className="px-3 py-1.5 rounded-lg text-white text-xs font-bold" style={{ background: base }}>Base</span>
          <Button type="button" size="sm" variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 ml-auto"
            onClick={() => setB({ color_base: base, ...sug })} data-testid="sugerir-paleta-btn">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Sugerir paleta
          </Button>
        </ColorRow>
        <ColorRow title="✨ Acento" desc="Botón de envío, checkboxes y estados seleccionados"
          value={b.color_acento} fallback={base} testid="design-color-acento"
          onChange={(v) => setB({ color_acento: v })} onReset={() => setB({ color_acento: sug.color_acento })}>
          <span className="px-3 py-1.5 rounded-lg text-white text-xs font-bold" style={{ background: acento }}>Enviar</span>
        </ColorRow>
        <ColorRow title="🎩 Header y Footer" desc="Franja superior (donde van los logos) y footer inferior (Powered by PD Travel)"
          value={b.color_header} fallback="#0A1230" testid="design-color-header"
          onChange={(v) => setB({ color_header: v })} onReset={() => setB({ color_header: sug.color_header })}>
          <span className="px-3 py-1.5 rounded-lg text-white text-xs font-bold" style={{ background: header }}>Header</span>
          <span className="px-3 py-1.5 rounded-lg text-white text-xs font-bold" style={{ background: header }}>Footer</span>
        </ColorRow>
        <ColorRow title="💧 Suave" desc="Fondo sutil de la página del formulario"
          value={b.color_suave} fallback="#F4F6FB" testid="design-color-suave"
          onChange={(v) => setB({ color_suave: v })} onReset={() => setB({ color_suave: sug.color_suave })}>
          <span className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 text-slate-600" style={{ background: suave }}>Fondo suave</span>
        </ColorRow>
      </div>
      <div className="lg:sticky lg:top-0 self-start">
        <p className="text-[11px] font-bold tracking-[0.25em] text-slate-400 text-center mb-3">VISTA PREVIA DEL FORMULARIO</p>
        <FormPreview nombre={projDialog.nombre} cliente={projDialog.cliente_nombre}
          logoEmpresa={b.logo_empresa_id ? `${API}/viajeros-ext/logos/${b.logo_empresa_id}` : null}
          logoEvento={b.logo_evento_id ? `${API}/viajeros-ext/logos/${b.logo_evento_id}` : null}
          base={base} acento={acento} header={header} suave={suave} />
      </div>
    </div>
  );
};

export const SendLinkDialog = ({ project, onClose }) => {
  const [emails, setEmails] = useState((project.notificar_a || []).join(", "));
  const [mensaje, setMensaje] = useState("");
  const [mostrarPD, setMostrarPD] = useState(true);
  const [sending, setSending] = useState(false);
  const send = async () => {
    const list = emails.split(/[,;\s]+/).map((e) => e.trim()).filter((e) => e.includes("@"));
    if (!list.length) return toast.error("Indica al menos un correo válido");
    setSending(true);
    try {
      await axios.post(`${API}/viajeros-ext/projects/${project.id}/send-link`, { emails: list, mensaje, mostrar_logo_pd: mostrarPD }, { withCredentials: true });
      toast.success(`Link enviado a ${list.length} destinatario(s)`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar el correo");
    } finally {
      setSending(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="send-link-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-cyan-700" /> Enviar link por correo</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500 -mt-2">Se enviará el link del formulario <b>{project.nombre}</b> con branding PD Travel.</p>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Correos del cliente (separados por coma)</Label>
            <Input value={emails} placeholder="contacto@cliente.com, otro@cliente.com"
              onChange={(e) => setEmails(e.target.value)} data-testid="send-link-emails" />
            <p className="text-[11px] text-slate-400">Se precargan los correos de "Notificar a" del proyecto; puedes editarlos antes de enviar.</p>
          </div>
          <div className="space-y-1.5">
            <Label>Mensaje personalizado (opcional)</Label>
            <Textarea rows={3} value={mensaje} placeholder="Hola, te compartimos el formulario para registrar a tus invitados…"
              onChange={(e) => setMensaje(e.target.value)} data-testid="send-link-mensaje" />
          </div>
          <label className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 px-3.5 py-3 cursor-pointer">
            <Checkbox checked={mostrarPD} onCheckedChange={(v) => setMostrarPD(!!v)} className="mt-0.5" data-testid="send-link-logo-pd" />
            <span className="text-sm text-slate-600 leading-snug">
              <b>Mostrar logo pd travel</b> en el encabezado del correo.
              <span className="block text-[11px] text-slate-400 mt-0.5">Activado: logo PD a la izquierda y logos del proyecto a la derecha (el color del logo PD se elige automático según el color del proyecto). Desactivado: solo los logos del proyecto, a la izquierda.</span>
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={send} disabled={sending} className="bg-cyan-700 hover:bg-cyan-800" data-testid="send-link-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />} Enviar link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
